import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CartTab.css';
import { cartAPI, ordersAPI } from '../../services/api';
import { useToast } from '../../Components/Toast/Toast';
import EmptyCartIcon from '../../assets/Frame 1618873315.png';

const CartTab = () => {
  const [cartData, setCartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await cartAPI.getCart();
      if (response.success) {
        // Handle different response structures
        const items = response.data?.cart?.items || response.data?.items || [];
        // Filter out items with missing service data
        const validItems = items.filter(item => item && item.service);
        setCartData(validItems);
      } else {
        setError(response.message || 'Failed to load cart');
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      setError('Failed to load cart. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (serviceId) => {
    try {
      const response = await cartAPI.removeFromCart(serviceId);
      if (response.success) {
        setCartData(cartData.filter(item => item.service._id !== serviceId));
        toast.success('Item removed from cart');
      } else {
        toast.error('Failed to remove item from cart');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item from cart');
    }
  };

  const handleUpdateQuantity = async (serviceId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(serviceId);
      return;
    }

    try {
      const response = await cartAPI.updateCartItem(serviceId, newQuantity);
      if (response.success) {
        setCartData(cartData.map(item => 
          item.service._id === serviceId 
            ? { ...item, quantity: newQuantity }
            : item
        ));
      } else {
        toast.error('Failed to update quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error('Failed to update quantity');
    }
  };

  const handleCheckout = async () => {
    if (cartData.length === 0) {
      toast.warning('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await ordersAPI.createOrder('cod');
      if (response.success) {
        toast.success('Order placed successfully! You can now upload documents for your order.');
        // Clear cart
        await cartAPI.clearCart();
        setCartData([]);
        // Redirect to orders page
        navigate('/orders');
      } else {
        toast.error('Failed to place order. Please try again.');
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const subtotal = cartData.reduce((total, item) => total + (item.service.price * item.quantity), 0);
  const tax = Math.round(subtotal * 0.025); // 2.5% tax
  const grandTotal = subtotal + tax;

  if (loading) {
    return (
      <div className="cart-tab">
        <div className="cart-header">
          <h2>Your Cart</h2>
        </div>
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading cart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cart-tab">
        <div className="cart-header">
          <h2>Your Cart</h2>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchCart} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-tab">
      <div className="cart-header">
        <h2>Your Cart ({cartData.length} Service{cartData.length !== 1 ? 's' : ''})</h2>
      </div>
      
      <div className="cart-content">
        {cartData.length === 0 ? (
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <img src={EmptyCartIcon} alt="Empty Cart" />
            </div>
            <h3>Your cart is empty</h3>
            <p>Add services to your cart to get started</p>
            <button onClick={() => navigate('/services')} className="browse-services-btn">
              Browse Services
            </button>
          </div>
        ) : (
          <>
            <div className="cart-table-wrapper">
              <table className="cart-table">
                <thead>
                  <tr className="cart-table-header">
                    <th className="header-checkbox"></th>
                    <th className="header-service">Service</th>
                    <th className="header-quantity">Quantity</th>
                    <th className="header-price">Price</th>
                    <th className="header-total">Total</th>
                    <th className="header-remove"></th>
                  </tr>
                </thead>
                <tbody className="cart-items">
                  {cartData.map((item) => (
                    <tr key={item.service._id} className="cart-item">
                      <td className="item-checkbox">
                        <input type="checkbox" defaultChecked className="cart-checkbox" />
                      </td>
                      <td className="item-service">
                        <div className="service-name">{item?.service?.name}</div>
                        <div className="service-description">
                          {item.service.description || 'Service description not available'}
                        </div>
                      </td>
                      <td className="item-quantity">
                        <div className="quantity-controls">
                          <button 
                            onClick={() => handleUpdateQuantity(item.service._id, item.quantity - 1)}
                            className="quantity-btn minus"
                            type="button"
                          >
                            -
                          </button>
                          <span className="quantity-value">{item.quantity}</span>
                          <button 
                            onClick={() => handleUpdateQuantity(item.service._id, item.quantity + 1)}
                            className="quantity-btn plus"
                            type="button"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="item-price">₹{item.service.price.toLocaleString('en-IN')}</td>
                      <td className="item-total">₹{(item.service.price * item.quantity).toLocaleString('en-IN')}</td>
                      <td className="item-remove">
                        <button 
                          className="remove-btn"
                          onClick={() => handleRemoveItem(item.service._id)}
                          type="button"
                          title="Remove item"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        <div className="order-summary">
          <div className="summary-row">
            <span>Subtotal :</span>
            <span>₹{subtotal}</span>
          </div>
          <div className="summary-row">
            <span>Tax :</span>
            <span>₹{tax}</span>
          </div>
          <div className="summary-row grand-total">
            <span>Grand total :</span>
            <span>₹{grandTotal}</span>
          </div>
        </div>
        
        {cartData.length > 0 && (
          <div className="checkout-section">
            <button 
              className="checkout-btn"
              onClick={handleCheckout}
              disabled={checkoutLoading}
            >
              {checkoutLoading ? 'Processing...' : 'Checkout'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartTab;
